import React, { useEffect, useMemo, useState } from 'react';
import { Check, CircleDot, Grid2X2, Loader2, MousePointer2, Ruler, RotateCcw } from 'lucide-react';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Slider } from '../ui/slider';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { centerSpacingPx, formatDistance, scaleFromHexDistance } from './hexGridMath';

const panelCopy = {
  fr: {
    title: 'Calage de la carte', description: 'Réglages propres à cette carte', grid: 'Grille', scale: 'Échelle',
    active: 'Afficher', size: 'Rayon', orientation: 'Orientation', pointy: 'Sommet en haut', flat: 'Côté plat en haut',
    offset: 'Décalage', rotation: 'Rotation', appearance: 'Apparence', opacity: 'Opacité', thickness: 'Épaisseur',
    labels: 'Identifiants', numbering: 'Format', fourDigit: '0101', alpha: 'A1', continuous: 'Continu',
    align: 'Caler sur deux centres', alignHelp: 'Cliquez deux centres voisins sur la grille imprimée.',
    current: 'Échelle actuelle', perHex: 'par hexagone', perPixel: 'par pixel', direct: 'Définir directement',
    directHelp: 'Distance entre deux centres voisins.', apply: 'Appliquer', segment: 'Calibrer avec un segment',
    segmentHelp: 'Tracez une distance connue sur la carte puis saisissez sa valeur.', useDefault: "Utiliser l’ordre de grandeur de l’univers",
    noScale: 'Échelle non définie', saved: 'Enregistré', saving: 'Enregistrement…', unsaved: 'Modifié',
    shortcuts: 'Raccourcis', shortcutText: 'Alt + molette : taille · Flèches : décalage précis · Maj + flèches : 10 px · G : masquer · F : vue générale',
    resetVisual: 'Réinitialiser le style', measured: 'Segment mesuré', pixels: 'pixels', cancel: 'Annuler le pointage',
  },
  en: {
    title: 'Map alignment', description: 'Settings stored for this map', grid: 'Grid', scale: 'Scale',
    active: 'Display', size: 'Radius', orientation: 'Orientation', pointy: 'Pointy top', flat: 'Flat top',
    offset: 'Offset', rotation: 'Rotation', appearance: 'Appearance', opacity: 'Opacity', thickness: 'Thickness',
    labels: 'Identifiers', numbering: 'Format', fourDigit: '0101', alpha: 'A1', continuous: 'Continuous',
    align: 'Align from two centers', alignHelp: 'Click two neighboring centers on the printed grid.',
    current: 'Current scale', perHex: 'per hex', perPixel: 'per pixel', direct: 'Set directly',
    directHelp: 'Distance between two neighboring centers.', apply: 'Apply', segment: 'Calibrate with a segment',
    segmentHelp: 'Draw a known distance on the map, then enter its value.', useDefault: 'Use the universe range',
    noScale: 'Scale not set', saved: 'Saved', saving: 'Saving…', unsaved: 'Changed',
    shortcuts: 'Shortcuts', shortcutText: 'Alt + wheel: size · Arrows: precise offset · Shift + arrows: 10 px · G: hide · F: fit',
    resetVisual: 'Reset style', measured: 'Measured segment', pixels: 'pixels', cancel: 'Cancel pointing',
  },
};

const numericValue = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export function TravelCalibrationPanel({
  calibration,
  language = 'fr',
  distanceUnit = 'km',
  universeDefaultMetersPerHex,
  interactionMode,
  onInteractionModeChange,
  measuredSegment,
  onMeasuredSegmentClear,
  onCalibrationChange,
  saveStatus = 'saved',
}) {
  const text = panelCopy[language] || panelCopy.fr;
  const { grid, scale } = calibration;
  const distancePerHexMeters = scale.metersPerPixel
    ? scale.metersPerPixel * centerSpacingPx(grid)
    : null;
  const displayDivisor = distanceUnit === 'mi' ? 1609.344 : 1000;
  const [directDistance, setDirectDistance] = useState('');
  const [segmentDistance, setSegmentDistance] = useState('');

  useEffect(() => {
    if (distancePerHexMeters) setDirectDistance((distancePerHexMeters / displayDivisor).toFixed(2));
  }, [displayDivisor, distancePerHexMeters]);

  const status = useMemo(() => ({
    saved: { label: text.saved, icon: Check, className: 'text-emerald-600 dark:text-emerald-300' },
    saving: { label: text.saving, icon: Loader2, className: 'text-amber-600 dark:text-amber-300' },
    dirty: { label: text.unsaved, icon: CircleDot, className: 'text-muted-foreground' },
  })[saveStatus] || { label: text.saved, icon: Check, className: 'text-emerald-600' }, [saveStatus, text]);
  const StatusIcon = status.icon;

  const setGrid = (changes) => onCalibrationChange({
    ...calibration,
    grid: { ...grid, ...changes },
  });
  const setScale = (changes) => onCalibrationChange({
    ...calibration,
    scale: { ...scale, ...changes },
  });

  const applyDirectScale = () => {
    const displayDistance = numericValue(directDistance);
    if (displayDistance <= 0) return;
    const meters = displayDistance * displayDivisor;
    setScale({
      metersPerPixel: scaleFromHexDistance(meters, grid),
      calibrationMethod: 'direct',
      referencePixelLength: centerSpacingPx(grid),
      referenceDistanceMeters: meters,
    });
  };

  const applySegmentScale = () => {
    const displayDistance = numericValue(segmentDistance);
    if (!measuredSegment?.pixelLength || displayDistance <= 0) return;
    const meters = displayDistance * displayDivisor;
    setScale({
      metersPerPixel: meters / measuredSegment.pixelLength,
      calibrationMethod: 'segment',
      referencePixelLength: measuredSegment.pixelLength,
      referenceDistanceMeters: meters,
    });
    onMeasuredSegmentClear();
    setSegmentDistance('');
  };

  const applyUniverseDefault = () => {
    if (!universeDefaultMetersPerHex) return;
    setScale({
      metersPerPixel: scaleFromHexDistance(universeDefaultMetersPerHex, grid),
      calibrationMethod: 'universe_default',
      referencePixelLength: centerSpacingPx(grid),
      referenceDistanceMeters: universeDefaultMetersPerHex,
    });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base"><Grid2X2 className="h-4 w-4" />{text.title}</CardTitle>
            <CardDescription>{text.description}</CardDescription>
          </div>
          <Badge variant="outline" className={status.className}>
            <StatusIcon className={`mr-1 h-3 w-3 ${saveStatus === 'saving' ? 'animate-spin' : ''}`} />{status.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="grid">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="grid">{text.grid}</TabsTrigger>
            <TabsTrigger value="scale">{text.scale}</TabsTrigger>
          </TabsList>

          <TabsContent value="grid" className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="travel-grid-enabled">{text.active}</Label>
              <Switch id="travel-grid-enabled" checked={grid.enabled} onCheckedChange={(enabled) => setGrid({ enabled })} />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between"><Label>{text.size}</Label><span className="text-xs tabular-nums text-muted-foreground">{Math.round(grid.sizePx)} px</span></div>
              <Slider value={[grid.sizePx]} min={8} max={220} step={1} onValueChange={([sizePx]) => setGrid({ sizePx })} />
            </div>

            <div className="space-y-2">
              <Label>{text.orientation}</Label>
              <Select value={grid.orientation} onValueChange={(orientation) => setGrid({ orientation })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pointy">{text.pointy}</SelectItem>
                  <SelectItem value="flat">{text.flat}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{text.offset}</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input type="number" step="1" value={Math.round(grid.offsetX * 100) / 100} onChange={(event) => setGrid({ offsetX: numericValue(event.target.value, grid.offsetX) })} aria-label="Offset X" />
                <Input type="number" step="1" value={Math.round(grid.offsetY * 100) / 100} onChange={(event) => setGrid({ offsetY: numericValue(event.target.value, grid.offsetY) })} aria-label="Offset Y" />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between"><Label>{text.rotation}</Label><span className="text-xs tabular-nums text-muted-foreground">{grid.rotationDeg.toFixed(1)}°</span></div>
              <Slider value={[grid.rotationDeg]} min={-30} max={30} step={0.1} onValueChange={([rotationDeg]) => setGrid({ rotationDeg })} />
            </div>

            <Button
              className="w-full"
              variant={interactionMode === 'grid' ? 'default' : 'outline'}
              onClick={() => onInteractionModeChange(interactionMode === 'grid' ? 'navigate' : 'grid')}
            >
              <MousePointer2 />{interactionMode === 'grid' ? text.cancel : text.align}
            </Button>
            <p className="-mt-2 text-xs text-muted-foreground">{text.alignHelp}</p>

            <div className="space-y-3 rounded-lg border border-border p-3">
              <Label>{text.appearance}</Label>
              <div className="grid grid-cols-[44px_1fr] items-center gap-3">
                <input type="color" value={grid.lineColor} onChange={(event) => setGrid({ lineColor: event.target.value })} className="h-9 w-11 cursor-pointer rounded border border-input bg-transparent p-1" aria-label={text.appearance} />
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-muted-foreground"><span>{text.opacity}</span><span>{Math.round(grid.opacity * 100)}%</span></div>
                  <Slider value={[grid.opacity]} min={0.05} max={1} step={0.05} onValueChange={([opacity]) => setGrid({ opacity })} />
                </div>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground"><span>{text.thickness}</span><span>{grid.lineWidth.toFixed(1)} px</span></div>
                <Slider value={[grid.lineWidth]} min={0.5} max={4} step={0.5} onValueChange={([lineWidth]) => setGrid({ lineWidth })} />
              </div>
              <Button variant="ghost" size="sm" className="w-full" onClick={() => setGrid({ lineColor: '#F4E8C1', lineWidth: 1, opacity: 0.55 })}>
                <RotateCcw />{text.resetVisual}
              </Button>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="travel-grid-labels">{text.labels}</Label>
              <Switch id="travel-grid-labels" checked={grid.showLabels} onCheckedChange={(showLabels) => setGrid({ showLabels })} />
            </div>
            {grid.showLabels && (
              <div className="space-y-2">
                <Label>{text.numbering}</Label>
                <Select value={grid.numbering} onValueChange={(numbering) => setGrid({ numbering })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="four_digit">{text.fourDigit}</SelectItem>
                    <SelectItem value="alpha_numeric">{text.alpha}</SelectItem>
                    <SelectItem value="continuous">{text.continuous}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </TabsContent>

          <TabsContent value="scale" className="mt-4 space-y-4">
            <div className="rounded-lg border border-border bg-muted/25 p-3">
              <p className="text-xs text-muted-foreground">{text.current}</p>
              {scale.metersPerPixel ? (
                <>
                  <p className="mt-1 text-lg font-semibold">{formatDistance(distancePerHexMeters, distanceUnit, language)} <span className="text-xs font-normal text-muted-foreground">{text.perHex}</span></p>
                  <p className="text-xs text-muted-foreground">{scale.metersPerPixel.toPrecision(5)} m {text.perPixel}</p>
                </>
              ) : <p className="mt-1 text-sm font-medium">{text.noScale}</p>}
            </div>

            <Button variant="outline" className="w-full" onClick={applyUniverseDefault} disabled={!universeDefaultMetersPerHex}>
              <CompassIcon />{text.useDefault}
            </Button>

            <div className="space-y-2 rounded-lg border border-border p-3">
              <Label>{text.direct}</Label>
              <p className="text-xs text-muted-foreground">{text.directHelp}</p>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input type="number" min="0" step="0.01" value={directDistance} onChange={(event) => setDirectDistance(event.target.value)} className="pr-12" />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{distanceUnit}</span>
                </div>
                <Button onClick={applyDirectScale}>{text.apply}</Button>
              </div>
            </div>

            <div className="space-y-2 rounded-lg border border-border p-3">
              <Label>{text.segment}</Label>
              <p className="text-xs text-muted-foreground">{text.segmentHelp}</p>
              <Button
                variant={interactionMode === 'scale' ? 'default' : 'outline'}
                className="w-full"
                onClick={() => onInteractionModeChange(interactionMode === 'scale' ? 'navigate' : 'scale')}
              >
                <Ruler />{interactionMode === 'scale' ? text.cancel : text.segment}
              </Button>
              {measuredSegment && (
                <div className="space-y-2 pt-2">
                  <p className="text-xs font-medium">{text.measured}: {measuredSegment.pixelLength.toFixed(1)} {text.pixels}</p>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input type="number" min="0" step="0.01" value={segmentDistance} onChange={(event) => setSegmentDistance(event.target.value)} className="pr-12" autoFocus />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{distanceUnit}</span>
                    </div>
                    <Button onClick={applySegmentScale}>{text.apply}</Button>
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-dashed border-border p-3">
              <p className="text-xs font-semibold">{text.shortcuts}</p>
              <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{text.shortcutText}</p>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

function CompassIcon() {
  return <Ruler className="h-4 w-4" />;
}

export default TravelCalibrationPanel;
